import React, {useMemo} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {PersonModelInterface} from "@/models/personModel";

import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {Alert, AlertDescription} from "@/components/ui/alert";

import {
    Smartphone,
    BadgePercent,
    CalendarDays,
    Coins,
    Banknote,
    Receipt,
} from "lucide-react";

type FieldError = { message?: string } | undefined;

const months = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const ErrorText = ({err}: { err?: FieldError }) => {
    if (!err?.message) return null;
    return (
        <p className="text-xs text-destructive mt-1 leading-snug">{err.message}</p>
    );
};

const Field = ({
                   icon: Icon,
                   label,
                   children,
                   hint,
               }: {
    icon: React.ElementType;
    label: string;
    children: React.ReactNode;
    hint?: string;
}) => (
    <div className="space-y-2">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground"/>
            <Label className="text-sm">{label}</Label>
        </div>
        {children}
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
);

const FormFields: React.FC = () => {
    const {
        register,
        setValue,
        formState: {errors},
    } = useFormContext<PersonModelInterface>();

    const phoneAmount = useWatch({
        name: "creditDetail.phoneAmount",
    }) as number | undefined;

    const term = useWatch({
        name: "creditDetail.term",
    }) as number | undefined;

    const phonePrice = Number(phoneAmount || 0);

    const termLabel = useMemo(() => {
        const t = Number(term || 0);
        if (!t) return "Выберите срок";
        const suffix = t === 1 ? "месяц" : "месяцев";
        return `${t} ${suffix}`;
    }, [term]);

    return (
        <Card className="rounded-2xl">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <Receipt className="h-4 w-4"/>
                    Параметры кредита
                </CardTitle>
            </CardHeader>

            <CardContent className="space-y-6">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field
                        icon={Smartphone}
                        label="Сумма телефона"
                        hint="Укажите стоимость телефона"
                    >
                        <Input
                            type="number"
                            inputMode="numeric"
                            placeholder="Например: 35000"
                            {...register("creditDetail.phoneAmount", {
                                required: "Обязательное поле",
                                valueAsNumber: true,
                                min: {value: 1, message: "Значение не может быть меньше 1"},
                            })}
                        />
                        <ErrorText err={errors.creditDetail?.phoneAmount as any}/>
                    </Field>

                    <Field
                        icon={Banknote}
                        label="Первоначальный взнос"
                        hint="Не должен быть равен или больше суммы телефона"
                    >
                        <Input
                            type="number"
                            inputMode="numeric"
                            placeholder="Например: 5000"
                            {...register("creditDetail.downPayment", {
                                required: "Обязательное поле",
                                valueAsNumber: true,
                                min: {value: 0, message: "Значение не может быть отрицательным"},
                                validate: (value: number) =>
                                    value < phonePrice ||
                                    "Первоначальный взнос не может быть равен или превышать стоимость телефона",
                            })}
                        />
                        <ErrorText err={errors.creditDetail?.downPayment as any}/>
                    </Field>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field icon={CalendarDays} label="Срок" hint="Выберите срок в месяцах">
                        <Select
                            value={term ? String(term) : ""}
                            onValueChange={(v: any) =>
                                setValue("creditDetail.term", Number(v), {
                                    shouldDirty: true,
                                    shouldTouch: true,
                                    shouldValidate: true,
                                })
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder={termLabel}/>
                            </SelectTrigger>
                            <SelectContent>
                                {months.map((m) => (
                                    <SelectItem key={m} value={String(m)}>
                                        {m} {m === 1 ? "месяц" : "месяцев"}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <ErrorText err={errors.creditDetail?.term as any}/>
                    </Field>

                    <Field
                        icon={BadgePercent}
                        label="Процент"
                        hint="Процент наценки (не годовой), как у тебя в расчёте"
                    >
                        <Input
                            type="number"
                            inputMode="decimal"
                            placeholder="Например: 30"
                            {...register("creditDetail.percent", {
                                required: "Обязательное поле",
                                valueAsNumber: true,
                            })}
                        />
                        <ErrorText err={errors.creditDetail?.percent as any}/>
                    </Field>
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field icon={CalendarDays} label="Дата кредитования">
                        <Input
                            type="date"
                            {...register("creditDetail.releaseDay", {
                                required: "Обязательное поле",
                            })}
                        />
                        <ErrorText err={errors.creditDetail?.releaseDay as any}/>
                    </Field>

                    <Field
                        icon={Coins}
                        label="Себестоимость"
                        hint="Не должна превышать стоимость телефона"
                    >
                        <Input
                            type="number"
                            inputMode="numeric"
                            placeholder="Например: 28000"
                            {...register("phone.costPrice", {
                                required: "Обязательное поле",
                                valueAsNumber: true,
                                min: {value: 0, message: "Значение не может быть отрицательным"},
                                validate: (value: number) =>
                                    value < phonePrice || "Себестоимость не может превышать стоимость телефона",
                            })}
                        />
                        <ErrorText err={errors.phone?.costPrice as any}/>
                    </Field>
                </div>

                <Alert>
                    <AlertDescription className="text-sm">
                        Расчёт идёт как у тебя: (сумма телефона минус взнос) плюс процент, затем
                        добавляется взнос.
                    </AlertDescription>
                </Alert>
            </CardContent>
        </Card>
    );
};

export default FormFields;
